import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import Lightbox from '../components/Lightbox';
import ScrollReveal from '../components/ScrollReveal';
import defaultImages from '../../images.json';

export default function Gallery() {
  const [allItems, setAllItems] = useState(defaultImages || []);
  const [activeCategory, setActiveCategory] = useState('alle');
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    fetch('https://api.dev.auxo.dk/public/content/galleri')
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data) && data.length > 0) {
          // Merge or use CMS data, ensuring all have formatted fields
          const formatted = data
            .filter(item => {
              const url = Array.isArray(item.billede) ? item.billede[0] : (item.billede || '');
              return !url.includes('f0cee536-6343-45a6-8f18-8f142baa11a3') && !url.includes('autoriseret-asbest') && item.id !== 'f0cee536-6343-45a6-8f18-8f142baa11a3';
            })
            .map(item => ({
              ...item,
              titel: item.titel || '',
              kategori: item.kategori || '',
              kategori_slug: item.kategori_slug || 'tag-facade',
              billede: Array.isArray(item.billede) ? item.billede[0] : item.billede,
              beskrivelse: item.beskrivelse || defaultImages.find(d => d.url === (Array.isArray(item.billede) ? item.billede[0] : item.billede))?.beskrivelse || ''
            }));
          setAllItems(formatted);
        }
      })
      .catch(err => {
        console.error('Fejl ved hentning af galleri:', err);
      });
  }, []);

  const categories = [
    { id: 'alle', name: 'Alle projekter' },
    { id: 'tag-facade', name: 'Tag & Facade' },
    { id: 'interior-gulve', name: 'Interiør & Gulve' },
    { id: 'koekken-inventar', name: 'Køkken & Inventar' },
    { id: 'terrasse-udendoers', name: 'Terrasser & Udeliv' }
  ];

  const filteredItems = activeCategory === 'alle'
    ? allItems
    : allItems.filter(item => item.kategori_slug === activeCategory);

  return (
    <>
      <SEO 
        title="Referencer & Galleri" 
        description="Se udvalgte billeder af tømrerprojekter udført af Fyns - Tømreren ApS: Tagarbejde, facadebeklædning, træterrasser, køkkenmontering, sildebensparket og autoriseret asbestsanering på Fyn." 
      />

      {/* Header */}
      <section className="bg-[#1F2A37] text-white py-16 text-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-4">
          <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight">
            Projektgalleri
          </h1>
          <p className="text-gray-300 max-w-2xl mx-auto text-base leading-relaxed">
            Gå på opdagelse i vores seneste tømrer- og snedkerprojekter på Fyn udført af <Link to="/" className="text-white hover:text-gray-200 underline decoration-1 underline-offset-2 font-semibold" title="Gå til forsiden for Fyns Tømreren">Fyns - Tømreren ApS</Link>. Klik på et billede for at se det i fuld størrelse med detaljer.
          </p>
        </div>
      </section>

      {/* Filter & Grid */}
      <section className="py-16 bg-[#F5F5F5] min-h-[600px]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Category Filter Buttons */}
          <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 mb-12">
            {categories.map((cat) => {
              const count = cat.id === 'alle' ? allItems.length : allItems.filter(i => i.kategori_slug === cat.id).length;
              return (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`px-5 py-2.5 text-sm font-semibold transition-all duration-200 cursor-pointer ${
                    activeCategory === cat.id
                      ? 'bg-[#d75d5a] text-white shadow-md'
                      : 'bg-white text-gray-700 hover:bg-gray-200 border border-gray-200'
                  }`}
                >
                  {cat.name} ({count})
                </button>
              );
            })}
          </div>

          {/* Gallery Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredItems.map((item, idx) => {
              const rawImgUrl = Array.isArray(item.billede) ? item.billede[0] : (item.billede || item.url);
              const resizedImg = rawImgUrl ? rawImgUrl.replace('assets.auxodev.dk/assets/', 'assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/') : '';

              return (
                <ScrollReveal
                  key={item.id || idx}
                  animation="scale-up"
                  delay={(idx % 6) * 80}
                  onClick={() => setLightboxIndex(idx)}
                  className="group bg-white overflow-hidden cursor-pointer shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-200 flex flex-col"
                >
                  <figure className="flex flex-col h-full">
                    <div className="relative h-64 overflow-hidden bg-gray-200">
                      <img
                        src={resizedImg}
                        alt={`${item.titel} – ${item.beskrivelse || 'Udført tømrerarbejde på Fyn af Fyns Tømreren'}`}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />

                      {/* Zoom indicator icon */}
                      <div className="absolute top-4 right-4 w-9 h-9 bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 shadow-md">
                        <i className="fa-solid fa-expand text-sm"></i>
                      </div>
                    </div>

                    {/* Visible image description card */}
                    <figcaption className="p-4 bg-white border-t border-gray-100 flex-1 flex flex-col justify-between">
                      <div>
                        <span className="text-[11px] font-bold uppercase tracking-wider text-[#d75d5a] block mb-1">
                          {item.kategori || 'Tømrerarbejde'}
                        </span>
                        <h3 className="text-sm font-bold text-[#111111] group-hover:text-[#3A5A40] transition-colors leading-snug line-clamp-2">
                          {item.titel}
                        </h3>
                        {item.beskrivelse && (
                          <p className="text-xs text-gray-500 mt-1.5 line-clamp-2 leading-relaxed">
                            {item.beskrivelse}
                          </p>
                        )}
                      </div>
                      <div className="pt-2 text-[11px] text-[#3A5A40] font-semibold flex items-center gap-1">
                        <span>Se billede</span>
                        <i className="fa-solid fa-arrow-right text-[10px]"></i>
                      </div>
                    </figcaption>
                  </figure>
                </ScrollReveal>
              );
            })}
          </div>

          {filteredItems.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              Ingen billeder fundet i denne kategori.
            </div>
          )}

        </div>
      </section>

      {/* Lightbox Modal */}
      {lightboxIndex !== null && (
        <Lightbox
          images={filteredItems}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onPrev={() => setLightboxIndex((prev) => (prev > 0 ? prev - 1 : filteredItems.length - 1))}
          onNext={() => setLightboxIndex((prev) => (prev < filteredItems.length - 1 ? prev + 1 : 0))}
        />
      )}
    </>
  );
}
